import { valuationMessageFor } from "../../../services/valuation";
import { pluralize } from "../../../utils";
import { MISSING_VALUE } from "../../Settings/GoalsSection/constants";
import { HEALTH_TARGET_FIELDS } from "../../Settings/GoalsSection/healthTargetsConfig";
import {
  HealthTargetComparator,
  HealthTargetMetric,
} from "../../Settings/GoalsSection/types";
import {
  calculateDebtToAssetRatio,
  calculateInvestmentRunway,
  calculateSavingsRate,
} from "../utils";
import {
  DebtToAssetMetricInputs,
  PlanMetric,
  PlanMetricStatus,
  RunwayMetricInputs,
  YearPlanMetricInputs,
} from "./types";

export const PLAN_UNIT_LINES: Record<HealthTargetMetric, string> = {
  savingsRate: "of income · this year",
  runway: "months of spending covered",
  debtToAsset: "debt vs what you own",
  burnRate: "spending vs income · this year",
};

export const PLAN_STATUS_COLOR: Record<PlanMetricStatus, string> = {
  met: "var(--color-status-success)",
  missed: "var(--color-status-warning)",
  none: "var(--color-text-secondary)",
};

const YEAR_UNAVAILABLE_LINE = "This year's figures are unavailable";
const NO_SPENDING_LINE = "No spending yet";
const NOTHING_OWNED_LINE = "Nothing owned yet";
const PERCENT_DECIMALS = 1;
const RUNWAY_DECIMALS = 1;
const DEBT_DECIMALS = 2;
const JANUARY = 0;

const roundTo = (value: number, decimals: number) =>
  Number(value.toFixed(decimals));

const unavailable = (unitLine: string): PlanMetric => ({
  value: null,
  displayValue: MISSING_VALUE,
  fill: 0,
  status: "none",
  unitLine,
  detail: null,
});

interface Assessment {
  status: PlanMetricStatus;
  gap: number;
}

const isMet = (
  value: number,
  target: number,
  comparator: HealthTargetComparator,
) => {
  if (comparator === "at least") {
    return value >= target;
  }
  if (comparator === "at most") {
    return value <= target;
  }
  return value < target;
};

const assess = (
  value: number,
  target: number | null,
  comparator: HealthTargetComparator,
  decimals: number,
): Assessment => {
  if (target === null) {
    return { status: "none", gap: 0 };
  }
  const gap = comparator === "at least" ? target - value : value - target;
  return {
    status: isMet(value, target, comparator) ? "met" : "missed",
    gap: roundTo(gap, decimals),
  };
};

const detailOf = (
  status: PlanMetricStatus,
  gap: number,
  describeGap: (gap: number) => string,
): string | null => {
  if (status === "none") {
    return null;
  }
  if (status === "met") {
    return "Met";
  }
  return describeGap(gap);
};

const isTooEarly = (today: Date) => today.getMonth() === JANUARY;

const tooEarlyLine = (today: Date) => `Too early in ${today.getFullYear()}`;

export const savingsRateMetric = ({
  year,
  today,
  target,
  hasActivity,
}: YearPlanMetricInputs): PlanMetric => {
  const unitLine = PLAN_UNIT_LINES.savingsRate;
  if (!hasActivity) {
    return unavailable(unitLine);
  }
  if (isTooEarly(today)) {
    return unavailable(tooEarlyLine(today));
  }
  if (year === null) {
    return unavailable(YEAR_UNAVAILABLE_LINE);
  }
  if (year.income <= 0) {
    return unavailable(unitLine);
  }
  const value = roundTo(
    calculateSavingsRate(year.income, year.expenses),
    PERCENT_DECIMALS,
  );
  const { status, gap } = assess(value, target, "at least", PERCENT_DECIMALS);
  return {
    value,
    displayValue: `${value.toFixed(PERCENT_DECIMALS)}%`,
    fill: value / 100,
    status,
    unitLine,
    detail: detailOf(
      status,
      gap,
      (points) => `${points} ${pluralize(points, "pt")} under`,
    ),
  };
};

export const burnRateMetric = ({
  year,
  today,
  target,
  hasActivity,
}: YearPlanMetricInputs): PlanMetric => {
  const unitLine = PLAN_UNIT_LINES.burnRate;
  if (!hasActivity) {
    return unavailable(unitLine);
  }
  if (isTooEarly(today)) {
    return unavailable(tooEarlyLine(today));
  }
  if (year === null) {
    return unavailable(YEAR_UNAVAILABLE_LINE);
  }
  if (year.income <= 0) {
    return unavailable(unitLine);
  }
  const value = roundTo((year.expenses / year.income) * 100, PERCENT_DECIMALS);
  const { status, gap } = assess(value, target, "below", PERCENT_DECIMALS);
  return {
    value,
    displayValue: `${value.toFixed(PERCENT_DECIMALS)}%`,
    fill: Math.min(value / 100, 1),
    status,
    unitLine,
    detail: detailOf(status, gap, (points) =>
      points === 0
        ? "On the limit"
        : `${points} ${pluralize(points, "pt")} over`,
    ),
  };
};

export const runwayMetric = ({
  investments,
  allTimeExpenses,
  numberOfMonths,
  valuation,
  target,
  hasActivity,
}: RunwayMetricInputs): PlanMetric => {
  if (!hasActivity) {
    return unavailable(PLAN_UNIT_LINES.runway);
  }
  if (investments === null) {
    return unavailable(valuationMessageFor(valuation));
  }
  if (allTimeExpenses <= 0) {
    return unavailable(NO_SPENDING_LINE);
  }
  const monthlySpending = allTimeExpenses / Math.max(numberOfMonths, 1);
  const value = roundTo(
    calculateInvestmentRunway(investments, monthlySpending),
    RUNWAY_DECIMALS,
  );
  const { status, gap } = assess(value, target, "at least", RUNWAY_DECIMALS);
  const fill = target === null ? 0 : Math.min(value / target, 1);
  return {
    value,
    displayValue: `${value.toFixed(RUNWAY_DECIMALS)}m`,
    fill,
    status,
    unitLine: PLAN_UNIT_LINES.runway,
    detail: detailOf(
      status,
      gap,
      (months) => `${months} ${pluralize(months, "month")} short`,
    ),
  };
};

export const debtToAssetMetric = ({
  liabilities,
  accounts,
  investments,
  savings,
  valuation,
  target,
  hasActivity,
}: DebtToAssetMetricInputs): PlanMetric => {
  if (!hasActivity) {
    return unavailable(PLAN_UNIT_LINES.debtToAsset);
  }
  if (investments === null) {
    return unavailable(valuationMessageFor(valuation));
  }
  if (accounts + investments + savings <= 0) {
    return unavailable(NOTHING_OWNED_LINE);
  }
  const value = roundTo(
    calculateDebtToAssetRatio(liabilities, accounts, investments, savings),
    DEBT_DECIMALS,
  );
  const { status, gap } = assess(value, target, "at most", DEBT_DECIMALS);
  return {
    value,
    displayValue: `${value.toFixed(DEBT_DECIMALS)}×`,
    fill: Math.min(value, 1),
    status,
    unitLine: PLAN_UNIT_LINES.debtToAsset,
    detail: detailOf(status, gap, (excess) => `${excess}× over`),
  };
};

export const targetTextOf = (
  metric: HealthTargetMetric,
  target: number | null,
): string | null => {
  if (target === null) {
    return null;
  }
  const field = HEALTH_TARGET_FIELDS.find((item) => item.metric === metric);
  if (!field) {
    return null;
  }
  const separator = field.spacedUnit ? " " : "";
  return `${field.comparator} ${target}${separator}${field.unit}`;
};
